import { createApp } from 'vue';
import { pinia } from '@/stores';
import { createMyVuetify, resolveVuetifyLocale } from '@/plugins/vuetify';
import { log } from '@/utils/core/vueLogger.js';
import { iframeDataBridge } from '@/iframe/iframeDataBridge.js';
import { isSyncCompleted, isBackgroundSyncCompleted } from '@/sync/dataSync.js';
import { eventBus } from '@/utils/core/eventBus.js';
import { isEmbeddedInBackOffice } from '@/utils/core/boContext.js';

import WidgetLayout from '@/components/widgets/WidgetLayout.vue';
import CalendarMap from '@/components/map/CalendarMap.vue';
import POSMap from '@/components/map/POSMap.vue';
import KpiStatementZone from '@/components/kpi/KpiStatementZone.vue';
import PointsDialogZone from '@/components/points/PointsDialogZone.vue';
import DataSharing from '@/components/dataSharing/DataSharing.vue';
import FutureReferencingZone from '@/components/sales/FutureReferencingZone.vue';
import SalesPlanZone from '@/components/sales/SalesPlanZone.vue';
import PromoStepZone from '@/components/promo/PromoStepZone.vue';
import CatchmentAreaDefinition from '@/components/catchmentArea/CatchmentAreaDefinition.vue';
import StatementDialogsZone from '@/components/sales/StatementDialogsZone.vue';
import DeliveryLocationFiltersDialog from '@/components/dialogs/DeliveryLocationFiltersDialog.vue';
import DeliveryLocationDialog from '@/components/dialogs/DeliveryLocationDialog.vue';
import ProductPickerZone from '@/components/dialogs/ProductPickerZone.vue';
import ProductDetailZone from '@/components/dialogs/ProductDetailZone.vue';
import DeliveryLocationForm from '@/components/forms/DeliveryLocationForm.vue';
import CatalogCartZone from '@/components/nav/CatalogCartZone.vue';
import RouteOptimizerZone from '@/components/routeOptimizer/RouteOptimizerZone.vue';
import AppointmentPinZone from '@/components/routeOptimizer/AppointmentPinZone.vue';

let vuetifyStylesInjected = false;
let zoneAppCount = 0;

// Every zone is its own Vue app in the same iframe document : useId() restarts at v-0 in each of them, so two zones gave
// their inputs the same ids and an aria-labelledby pointed at another zone's label. A prefix per app keeps them apart.
export function createZoneApp(component, props) {
	const app = createApp(component, props);
	app.config.idPrefix = `z${++zoneAppCount}`;
	return app;
}

const activeIntervals = new Map();
const dynamicApps = new Map();
const zoneComponents = [
	{
		selector: 'vue-widgets-zone',
		component: WidgetLayout,
		persistent: true,
		// WidgetLayout shows its own skeleton while sync is in progress, so it can mount
		// as soon as the placeholder exists instead of waiting for sync to finish.
		mountWhileSyncing: true,
		// Never shown in the Back-Office (BO displays a single visit/order, not the home
		// dashboard). Skip mounting entirely there — otherwise WidgetLayout mounts, its
		// onMounted bails out for BO, and the template is left showing its loading skeleton
		// + 'widgetsLoading' snackbar forever.
		skipInBackOffice: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		selector: 'vue-calendar-map-zone',
		component: CalendarMap,
		persistent: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		selector: 'vue-kpi-statement-zone',
		component: KpiStatementZone,
		// Not used in the Back-Office visit consultation (its placeholder never appears there — the
		// legacy view renders the KPIs itself), so skip instead of polling 30s then giving up.
		skipInBackOffice: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		// "Référencement futur" tab of the visit Préparation step. Read-only, one instance per visit view, so a
		// static registration is enough. NOT skipped in the Back-Office: the tab is part of the visit consultation.
		selector: 'vue-future-referencing-zone',
		component: FutureReferencingZone,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		selector: 'vue-points-dialog-zone',
		component: PointsDialogZone,
		// Salesperson gamification dialog — irrelevant in a Back-Office consultation. Skip in BO.
		skipInBackOffice: true,
		isMounted: false,
		app: null,
		mountedEl: null,
		persistent: true,
	},
	{
		selector: 'vue-statement-dialogs-zone',
		component: StatementDialogsZone,
		// Relevé-only dialogs (catchment comparison, competitor positioning) — no Back-Office relevé screen to attach them to.
		skipInBackOffice: true,
		isMounted: false,
		app: null,
		mountedEl: null,
		persistent: true,
	},
	{
		selector: 'vue-delivery-location-filter-dialog-zone',
		component: DeliveryLocationFiltersDialog,
		persistent: true, // même comportement que vue-delivery-location-modal-zone : survit aux ré-rendus KO, filtres conservés entre pages
		mountWhileSyncing: true,
		skipInBackOffice: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		// Remplace app-wide la modale KO deliverylocation.htm (voir iframeWatcher.js#onModalDeliveryLocationOpened
		// et #emitMainSyncCompleted, qui la monte une fois à la racine de l'iframe, quelle que soit la vue).
		selector: 'vue-delivery-location-modal-zone',
		component: DeliveryLocationDialog,
		persistent: true, // survit aux ré-rendus KO, s'ouvre/se ferme via son propre v-model
		skipInBackOffice: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		// #23521 replaces app-wide the legacy KO product modal (#mdPdt) : products.js keeps its filters and hands the list
		// to this zone (`el._openProductPicker`). Mounted once at the iframe root by iframeWatcher#emitMainSyncCompleted, also
		// in the Back-Office, since the legacy modal no longer renders anything.
		selector: 'vue-product-picker-zone',
		component: ProductPickerZone,
		persistent: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		// #23521 replaces app-wide the legacy KO product modal productAssortDetail (decommissioned) : the legacy views keep
		// publishing `evtModalProductAssortDetail`, this zone listens to it. Mounted once at the iframe root by
		// iframeWatcher#emitMainSyncCompleted, also in the Back-Office, since the legacy modal is gone.
		selector: 'vue-product-detail-zone',
		component: ProductDetailZone,
		persistent: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		// #23500 "Itinéraire optimisé" — opened from the agenda ⋮ menu (OPEN_ROUTE_OPTIMIZER), mounted once at the
		// iframe root by iframeWatcher#emitMainSyncCompleted.
		selector: 'vue-route-optimizer-zone',
		component: RouteOptimizerZone,
		persistent: true,
		skipInBackOffice: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		// #23500 "Épingler cette visite" switch inside the app-wide legacy appointment modal (appointment.htm).
		selector: 'vue-appointment-pin-zone',
		component: AppointmentPinZone,
		persistent: true,
		skipInBackOffice: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
	{
		// vwSFAPlanning — replaces the legacy "add_business" button (newDeliveryLocationButton) and
		// its menu: the component mounts as-is (button + menu + dialog), same injection pattern as
		// DeliveryLocationFiltersDialog above.
		selector: 'vue-delivery-location-form-zone',
		component: DeliveryLocationForm,
		persistent: true,
		skipInBackOffice: true,
		isMounted: false,
		app: null,
		mountedEl: null,
	},
];

/**
 * Inject Vuetify's dynamically generated styles into the target iframe document
 */
function injectVuetifyStyles() {
	const targetDocument = iframeDataBridge.getIframeWindow().document;
	const head = targetDocument.head;
	const isLocal = window.location.href.includes('localhost');

	[...targetDocument.querySelectorAll('[data-injected-style]')].forEach((e) => e.remove());

	const allStyleNodes = [...document.styleSheets].map((s) => s.ownerNode).filter(Boolean);
	const styles = isLocal ? allStyleNodes.filter((n) => n.tagName === 'STYLE' && (n.getAttribute('data-vite-dev-id') || n.innerText.includes('.v-'))) : [...document.querySelectorAll('link[rel="stylesheet"]')].filter((link) => link.href.includes('index') || link.href.includes('vuetify'));

	if (!styles.length) {
		log('warn', 'zoneMount', '⚠️ No Vuetify styles found in parent document');
	}

	styles.forEach((style) => {
		const clone = style.cloneNode(true);
		clone.setAttribute('data-injected-style', 'true');
		head.appendChild(clone);
	});

	const themeStyle = document.getElementById('vuetify-theme-stylesheet');

	if (themeStyle && !targetDocument.head.querySelector('#vuetify-theme-stylesheet')) {
		const clone = themeStyle.cloneNode(true);
		clone.setAttribute('data-injected-style', 'true');
		targetDocument.head.appendChild(clone);
		log('info', 'zoneMount', '✅ Injected Vuetify theme styles');
	}

	if (window.location.href.includes('localhost') && !vuetifyStylesInjected) {
		let reinjectTimeout;

		const observer = new MutationObserver(() => {
			clearTimeout(reinjectTimeout);
			reinjectTimeout = setTimeout(() => {
				const iframe = document.querySelector('iframe');
				if (iframe?.contentDocument) {
					log('info', 'zoneMount', '♻️ CSS updated — reinjecting into iframe');
					injectVuetifyStyles();
				}
			}, 200);
		});

		const styleNodes = [...document.querySelectorAll('style[data-vite-dev-id]')];

		styleNodes.forEach((style) => {
			observer.observe(style, {
				subtree: true,
				childList: true,
			});
		});
	}
}

/**
 * Injects Vuetify styles into the iframe document if that hasn't happened yet this load.
 * Exported so mount paths outside this module's own zone registry (e.g. src/mount/ecosFieldHost.js)
 * can share the same one-time injection instead of depending on another zone having mounted first.
 */
export function ensureVuetifyStyles() {
	if (!vuetifyStylesInjected) {
		injectVuetifyStyles();
		vuetifyStylesInjected = true;
	}
}

/**
 * Mounts a Vue component in a target element inside an iframe
 * @param {HTMLElement|null} el - Target element in iframe
 * @param {string} zoneSelector - The id of the element to mount into
 * @returns {boolean} - Whether the mount was successful
 */
export function mountElement(el, zoneSelector, options = {}) {
	const externis = iframeDataBridge.getExternis(false);

	const isSynchronizing = externis ? Object.values(externis.synchronizing).includes(true) : false;
	let zoneComponent = zoneComponents.find((zc) => zc.selector == zoneSelector);

	// Already mounted
	if (zoneComponent.isMounted) return true;

	if (!zoneComponent.mountWhileSyncing) {
		if (!isSyncCompleted()) {
			log('warn', 'zoneMount', `Mounting ${zoneSelector} skipped — main sync not yet completed.`);
			return false;
		}

		if (!isBackgroundSyncCompleted()) {
			log('warn', 'zoneMount', `Mounting ${zoneSelector} skipped — background sync not yet completed.`);
			return false;
		}

		if (isSynchronizing) {
			log('warn', 'zoneMount', `Mounting ${zoneSelector} skipped due to synchronizing state.`);
			return false;
		}
	}

	if (el) {
		const userLocale = resolveVuetifyLocale(iframeDataBridge.getCurrentUser()?.language);

		const app = createZoneApp(zoneComponent.component, options);
		const overlayRoot = el.ownerDocument.getElementById('vue-overlay-root');
		app.provide('overlayTarget', overlayRoot);
		app.use(pinia);
		app.use(createMyVuetify(userLocale));
		app.mount(el);

		zoneComponent.app = app;
		zoneComponent.isMounted = true;
		zoneComponent.mountedEl = el;

		return true;
	}

	return false;
}

/**
 * Repeatedly checks for the target zone in the iframe, and mounts the component once available.
 * Also handles remounting if the element is detached from DOM (e.g. by a KO ko:if).
 * Also injects Vuetify styles into the iframe
 * @param {string} zoneSelector - id of the mount target
 */
export function mountZone(zoneSelector, options = {}) {
	const iframeDocument = iframeDataBridge.getIframeWindow().document;
	const zone = zoneComponents.find((zc) => zc.selector === zoneSelector);
	const MAX_NOT_FOUND = 30;
	let notFoundCount = 0;

	if (zone?.skipInBackOffice && isEmbeddedInBackOffice()) {
		log('info', 'zoneMount', `Zone ${zoneSelector} skipped — embedded in Back-Office.`);
		return;
	}

	ensureVuetifyStyles();

	if (activeIntervals.has(zoneSelector)) {
		clearInterval(activeIntervals.get(zoneSelector));
	}

	const mountInterval = setInterval(() => {
		const el = iframeDocument.getElementById(zoneSelector);
		// If mounted but element was removed OR replaced by a new DOM node (e.g. KO rebinding)
		if (zone.isMounted && (!el || !el.isConnected || zone.mountedEl !== el)) {
			// Persistent zones: swap the cached mounted element back in place of the new empty element
			if (zone.persistent && el && zone.mountedEl && zone.mountedEl !== el) {
				el.parentNode?.replaceChild(zone.mountedEl, el);
				log('info', 'zoneMount', `♻️ Reattached persistent zone ${zoneSelector} via DOM swap`);
				clearInterval(mountInterval);
				activeIntervals.delete(zoneSelector);
				eventBus.emit('vue-persistent-zone-replaced', { component: zone.component, selector: zoneSelector });
				return;
			}
			if (zone.app) {
				zone.app.unmount();
				zone.app = null;
			}
			zone.isMounted = false;
			zone.mountedEl = null;
			notFoundCount = 0;
			log('warn', 'zoneMount', `Zone ${zoneSelector} was detached or replaced — will remount when element reappears`);
		}

		if (zone.isMounted) {
			clearInterval(mountInterval);
			activeIntervals.delete(zoneSelector);
			return;
		}

		if (!zone.isMounted) {
			if (!el) {
				notFoundCount++;
				if (notFoundCount >= MAX_NOT_FOUND) {
					clearInterval(mountInterval);
					activeIntervals.delete(zoneSelector);
					log('warn', 'zoneMount', `Zone ${zoneSelector} not found after ${MAX_NOT_FOUND}s — giving up.`);
					return;
				}
				if (notFoundCount === 1) {
					log('warn', 'zoneMount', `Element with selector ${zoneSelector} not found in iframe document. Retrying...`);
				}
			} else {
				notFoundCount = 0;
				mountElement(el, zoneSelector, options);
			}
		}
	}, 1000);

	activeIntervals.set(zoneSelector, mountInterval);
}

function mountDynamicZone(zoneId, component, { maxNotFound = 30, label = 'Dynamic zone', props = {} } = {}) {
	const iframeDocument = iframeDataBridge.getIframeWindow().document;
	let notFoundCount = 0;

	ensureVuetifyStyles();

	if (activeIntervals.has(zoneId)) {
		clearInterval(activeIntervals.get(zoneId));
	}

	const mountInterval = setInterval(() => {
		const el = iframeDocument.getElementById(zoneId);
		const state = dynamicApps.get(zoneId);

		if (state?.isMounted && (!el || !el.isConnected || state.mountedEl !== el)) {
			state.app.unmount();
			dynamicApps.delete(zoneId);
		}

		if (dynamicApps.get(zoneId)?.isMounted) {
			clearInterval(mountInterval);
			activeIntervals.delete(zoneId);
			return;
		}

		if (!el) {
			if (++notFoundCount >= maxNotFound) {
				clearInterval(mountInterval);
				activeIntervals.delete(zoneId);
				log('warn', 'zoneMount', `${label} ${zoneId} not found after ${maxNotFound}s — giving up.`);
			}
			return;
		}

		if (!isSyncCompleted() || !isBackgroundSyncCompleted()) return;

		const app = createZoneApp(component, props);
		app.provide('overlayTarget', el.ownerDocument.getElementById('vue-overlay-root'));
		app.use(pinia);
		app.use(createMyVuetify(resolveVuetifyLocale(iframeDataBridge.getCurrentUser()?.language)));
		app.mount(el);

		dynamicApps.set(zoneId, { app, isMounted: true, mountedEl: el });
		clearInterval(mountInterval);
		activeIntervals.delete(zoneId);
		log('info', 'zoneMount', `✅ ${label} mounted: ${zoneId}`);
	}, 500);

	activeIntervals.set(zoneId, mountInterval);
}

export const mountDynamicPointsZone = (zoneId) => mountDynamicZone(zoneId, PointsStatementZone, { maxNotFound: 15, label: 'Dynamic zone' });
export const mountDynamicPosMapZone = (zoneId) => mountDynamicZone(zoneId, POSMap, { maxNotFound: 30, label: 'Dynamic map zone', props: { zoneId } });
export const mountDynamicKpiPosZone = (zoneId) => mountDynamicZone(zoneId, KpiStatementZone, { maxNotFound: 30, label: 'Dynamic KPI zone' });
export const mountDynamicSalesPlanZone = (zoneId) => mountDynamicZone(zoneId, SalesPlanZone, { maxNotFound: 30, label: 'Dynamic sales plan zone' });
export const mountDynamicPromoStepZone = (zoneId) => mountDynamicZone(zoneId, PromoStepZone, { maxNotFound: 30, label: 'Dynamic promo step zone' });
export const mountDynamicCatchmentZone = (zoneId) => mountDynamicZone(zoneId, CatchmentAreaDefinition, { maxNotFound: 15, label: 'Dynamic catchment zone' });
export const mountDynamicDeliveryLocationZone = (zoneId, props = {}) => mountDynamicZone(zoneId, DeliveryLocationFiltersDialog, { maxNotFound: 15, label: 'Dynamic Delivery zone', props });
// BO hidden app (no navigation chrome): the cart in the catalogue header (vwSalesCatalog.js).
export const mountDynamicCatalogCartZone = (zoneId) => mountDynamicZone(zoneId, CatalogCartZone, { maxNotFound: 15, label: 'Dynamic catalog cart zone' });

export function mountDynamicDatasharZone(zoneId) {
	const iframeDocument = iframeDataBridge.getIframeWindow().document;
	let notFoundCount = 0;
	const MAX_NOT_FOUND = 30;

	ensureVuetifyStyles();

	if (activeIntervals.has(zoneId)) {
		clearInterval(activeIntervals.get(zoneId));
	}

	const mountInterval = setInterval(() => {
		const el = iframeDocument.getElementById(zoneId);
		const state = dynamicApps.get(zoneId);

		if (state?.isMounted && (!el || !el.isConnected || state.mountedEl !== el)) {
			state.app.unmount();
			dynamicApps.delete(zoneId);
		}

		if (!dynamicApps.get(zoneId)?.isMounted) {
			if (!el) {
				if (++notFoundCount >= MAX_NOT_FOUND) {
					clearInterval(mountInterval);
					activeIntervals.delete(zoneId);
					log('warn', 'zoneMount', `Dynamic datashar zone ${zoneId} not found after ${MAX_NOT_FOUND}s — giving up.`);
				}
				return;
			}

			if (!isSyncCompleted() || !isBackgroundSyncCompleted()) return;

			const app = createZoneApp(DataSharing);
			app.provide('overlayTarget', el.ownerDocument.getElementById('vue-overlay-root'));
			app.use(pinia);
			app.use(createMyVuetify(resolveVuetifyLocale(iframeDataBridge.getCurrentUser()?.language)));
			app.mount(el);

			dynamicApps.set(zoneId, { app, isMounted: true, mountedEl: el });
			clearInterval(mountInterval);
			activeIntervals.delete(zoneId);
			log('info', 'zoneMount', `✅ Dynamic datashar zone mounted: ${zoneId}`);
		}
	}, 500);

	activeIntervals.set(zoneId, mountInterval);
}

export function unmountAll() {
	const iframeDocument = iframeDataBridge.getIframeWindow().document;

	// Unmount dynamic per-campaign zones
	dynamicApps.forEach(({ app }, zoneId) => {
		if (app) {
			app.unmount();
			const el = iframeDocument.getElementById(zoneId);
			if (el) el.innerHTML = '';
			log('info', 'zoneMount', `🔌 Unmounted dynamic zone ${zoneId}`);
		}
	});
	dynamicApps.clear();

	// Clear intervals only for non-persistent zones
	for (const [selector, interval] of activeIntervals) {
		const zone = zoneComponents.find((zc) => zc.selector === selector);
		if (!zone?.persistent) {
			clearInterval(interval);
			activeIntervals.delete(selector);
		}
	}

	zoneComponents.forEach((zone) => {
		if (zone.persistent) return;
		if (zone.isMounted && zone.app) {
			zone.app.unmount();
			zone.app = null;
			zone.isMounted = false;
			zone.mountedEl = null;

			const el = iframeDocument.getElementById(zone.selector);
			if (el) el.innerHTML = '';
			log('info', 'zoneMount', `🔌 Unmounted ${zone.selector}`);
		}
	});

	// Keep styles if a persistent zone is still mounted
	const hasPersistentMounted = zoneComponents.some((z) => z.persistent && z.isMounted);
	if (!hasPersistentMounted) {
		[...iframeDocument.querySelectorAll('[data-injected-style]')].forEach((e) => e.remove());
		vuetifyStylesInjected = false;
	}

	log('success', 'zoneMount', '✅ All zones unmounted');

	// Remove overflow styles
	const contentWrapper = iframeDocument.querySelector('.content-wrapper');
	if (contentWrapper) {
		contentWrapper.style.overflow = '';
		contentWrapper.style.maxHeight = '';
		log('info', 'zoneMount', '✅ Cleared overflow styles from content-wrapper');
	}
}
